import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { useNotification } from "../../../contexts/NotificationContext";
import {
  GET_DMA_AVAILABILITY,
  UPDATE_DMA_AVAILABILITY,
} from "../../../graphql/dma-availability";

const parseCodes = (value: string) =>
  value
    .split(/[\s,]+/g)
    .map((code) => code.trim())
    .filter(Boolean);

export const AdminDmaAvailabilityPage = () => {
  const { showSuccess, showError } = useNotification();
  const { data, loading, error } = useQuery(GET_DMA_AVAILABILITY, {
    fetchPolicy: "cache-and-network",
  });
  const [updateAvailability, { loading: saving }] = useMutation(
    UPDATE_DMA_AVAILABILITY,
    {
      refetchQueries: [{ query: GET_DMA_AVAILABILITY }],
    }
  );

  const [inputValue, setInputValue] = useState("");
  const [hasEdited, setHasEdited] = useState(false);

  const existingCodes = useMemo(
    () => (data?.getDmaAvailability?.codes || []).map((code: string) => String(code)),
    [data]
  );

  useEffect(() => {
    if (loading || hasEdited) return;
    if (existingCodes.length) {
      setInputValue(existingCodes.join(", "));
    } else {
      setInputValue("");
    }
  }, [existingCodes, hasEdited, loading]);

  const parsedCodes = useMemo(() => parseCodes(inputValue), [inputValue]);

  const handleSave = async () => {
    try {
      await updateAvailability({ variables: { codes: parsedCodes } });
      showSuccess("DMA availability updated.");
      setHasEdited(false);
    } catch {
      showError("Unable to update DMA availability. Please try again.");
    }
  };

  return (
    <Box sx={{ minHeight: "100%" }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
            DMA Availability
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure which DMA codes are currently live for homeowner signup.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error">Unable to load DMA availability.</Alert>
        )}

        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #e5e7eb" }}>
          <Stack spacing={2}>
            <TextField
              label="Available DMA Codes"
              placeholder="e.g. 524, 501, 602"
              value={inputValue}
              onChange={(event) => {
                setInputValue(event.target.value);
                setHasEdited(true);
              }}
              helperText="Separate codes with commas or spaces."
              fullWidth
              multiline
              minRows={2}
            />

            <Stack direction="row" spacing={1} flexWrap="wrap">
              {parsedCodes.length ? (
                parsedCodes.map((code) => (
                  <Chip key={code} label={code} variant="outlined" />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No DMA codes configured yet.
                </Typography>
              )}
            </Stack>

            <Box>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading || saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default AdminDmaAvailabilityPage;
